// Leadership portrait crops — Figma 5890-27283 (cards) and 6436-28946 (modal).
// Portraits are hosted on the Sanity redesign dataset CDN (project 1aendizt).

export type PortraitFrame = {
  width: number;
  height: number;
  left: number;
  top: number;
  /** Inner image offset when the source asset needs horizontal centering (Aidan). */
  imageLeft?: string;
  imageTop?: string;
};

/** Pixel densities we serve a candidate for. */
const PORTRAIT_DPR_STEPS = [1, 2, 3] as const;

/**
 * Sanity CDN `src`/`srcSet`/`sizes` for a portrait drawn at `displayWidth` CSS px.
 *
 * The source assets are 3.5k–4k PNGs (John's is 16.7MB). Handing one of those
 * to the browser for a ~10–17x downscale makes it point-sample rather than
 * filter, which aliases fine garment patterns into moiré — John's checked
 * shirt was visibly affected at some viewport widths. Resizing on the CDN,
 * which filters properly, removes the artefact and ~99% of the payload.
 */
export function portraitImageProps(src: string, displayWidth: number) {
  const widths = PORTRAIT_DPR_STEPS.map((dpr) => Math.ceil(displayWidth * dpr));
  const url = (width: number) => `${src}?w=${width}&fm=webp&q=80&fit=max`;

  return {
    src: url(widths[0]!),
    srcSet: widths.map((width) => `${url(width)} ${width}w`).join(", "),
    sizes: `${Math.ceil(displayWidth)}px`,
  };
}

export type LeaderPortraitAssets = {
  cardSrc: string;
  modalSrc: string;
  card: {
    /** Figma 5890-27280 — full-width 331×220 frames at 375. */
    mobile: PortraitFrame;
    /** Figma 5890-27279 — 160×160 frames at 768. */
    tablet: PortraitFrame;
    /** Figma 5890-27281 — 358px-tall card frames at 1440. */
    desktop: PortraitFrame;
  };
  modal: PortraitFrame;
};

/** Team-member IDs in display order (Aidan, John, Deirdre). */
export const LEADERSHIP_MEMBER_IDS = [
  "944a16b7-fa07-4027-92e3-5ac009d08d7a",
  "df35d6b3-34ee-4f16-8d00-fa680fc61b6a",
  "d58f6da1-6f9c-4813-a200-51405ebe95c6",
] as const;

const AIDAN_PORTRAIT =
  "https://cdn.sanity.io/images/1aendizt/redesign/b845c0059f91b754080c92c4ca38baa29501f4ec-4096x4096.png";
const JOHN_PORTRAIT =
  "https://cdn.sanity.io/images/1aendizt/redesign/26e31f8a5017f7e456b5240958fae3716e1f543e-3511x4096.png";
const DEIRDRE_PORTRAIT =
  "https://cdn.sanity.io/images/1aendizt/redesign/cc8def1788658fc47607c6e1b0bd8e8340888ca0-3511x4096.png";

export const LEADERSHIP_PORTRAITS: Record<string, LeaderPortraitAssets> = {
  "944a16b7-fa07-4027-92e3-5ac009d08d7a": {
    cardSrc: AIDAN_PORTRAIT,
    modalSrc: AIDAN_PORTRAIT,
    card: {
      // Figma 5890-27280 — full-width mobile card frame (220px tall).
      // Wrapper is horizontally centered in the fluid w-full frame; imageLeft 0%
      // keeps the subject centered (desktop uses 3.77% with a fixed left offset).
      mobile: {
        width: 359.863,
        height: 359.863,
        left: -29.36,
        top: -31.6,
        imageLeft: "-4%",
        imageTop: "0.2%",
      },
      // Figma 5890-27279 — 160×160 tablet card frame
      tablet: {
        width: 225,
        height: 225,
        left: -44.5,
        top: -15.5,
        imageLeft: "3.77%",
        imageTop: "0.2%",
      },
      desktop: {
        width: 401,
        height: 401,
        left: -43.5,
        top: -22.5,
        imageLeft: "3.77%",
        imageTop: "0.2%",
      },
    },
    modal: {
      // Figma 6436-28866 inside 611×332 image frame
      width: 611,
      height: 611,
      left: -20,
      top: -51,
      imageLeft: "3.77%",
      imageTop: "0.2%",
    },
  },
  "df35d6b3-34ee-4f16-8d00-fa680fc61b6a": {
    cardSrc: JOHN_PORTRAIT,
    modalSrc: JOHN_PORTRAIT,
    card: {
      mobile: { width: 320.581, height: 374.012, left: -0.5, top: -34.96 },
      tablet: { width: 204, height: 238, left: -22.5, top: -11.5 },
      desktop: { width: 352, height: 411, left: -6.5, top: -27.5 },
    },
    modal: {
      width: 523,
      height: 611,
      left: 48,
      top: -46,
    },
  },
  "d58f6da1-6f9c-4813-a200-51405ebe95c6": {
    cardSrc: DEIRDRE_PORTRAIT,
    modalSrc: DEIRDRE_PORTRAIT,
    card: {
      mobile: { width: 305.925, height: 357.353, left: 12.04, top: -28.4 },
      tablet: { width: 232, height: 271, left: -38.5, top: -18.5 },
      desktop: { width: 390, height: 455, left: -38.5, top: -18.5 },
    },
    modal: {
      width: 619,
      height: 723,
      left: 0,
      top: -62,
    },
  },
};
